import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useContext } from 'react';
import ReactECharts from 'echarts-for-react';
import { color as echartsColor } from 'echarts';
import { forceSimulation, forceCenter, forceManyBody, forceCollide, scaleLinear, scaleTime, extent } from 'd3';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { sentimentPaletteMap, dashboardSentimentPaletteMap } from '../../utils/colors';
import { formatWithCustomGigaBillion, formatNumber } from '../../utils/fomatNumbers';
import { NarrativeStoriesModal } from '../../pages/NarrativePage/NarrativeStories/NarrativeStoriesModal';
import { AddToNarrativeModal } from '../AddToNarrativeModal/AddToNarrativeModal';
import { ThreatEditModal } from '../../pages/ThreatsPage/ThreatEditModal';
import { MessagesContext } from '../../pages/NarrativePage/contexts/MessagesContext';
import { API } from '../../API';
import { showRequestError } from '../../utils/requestError';
import { Sentiment } from '../Sentiment/Sentiment';
import { LoaderSmall } from '../LoaderSmall/LoaderSmall';
import { ExpandableText } from '../CollapsibleText/ExpandableText';
import { ReactComponent as OriginalTextIcon } from '../../assets/originalText.svg';
import { ReactComponent as TranslationIcon } from '../../assets/translation.svg';
import messageStyles from '../MessagesTable/ExtendedMessageView.module.scss';
import styles from './StoriesBubbleChart.module.scss';

const CHART_HEIGHT = 600;
const MIN_RADIUS = 15;
const MAX_RADIUS = 50;
const POPUP_WIDTH = 416; // 26rem at 16px; keep in sync with .popup width
const POPUP_GAP = 12;
const FALLBACK_FILL = '#eef1f6'; // --clr-gray200
const FALLBACK_STROKE = '#767b91'; // --clr-gray400

const CalendarIcon = () => (
  <svg viewBox="0 0 18 20" fill="none" aria-hidden="true">
    <path d="M17 8.2H1M12.5556 1V4.6M5.44444 1V4.6M5.26667 19H12.7333C14.2268 19 14.9735 19 15.544 18.7057C16.0457 18.4469 16.4537 18.0338 16.7094 17.5258C17 16.9482 17 16.1921 17 14.68V7.12C17 5.60786 17 4.85179 16.7094 4.27423C16.4537 3.76619 16.0457 3.35314 15.544 3.09428C14.9735 2.8 14.2268 2.8 12.7333 2.8H5.26667C3.77319 2.8 3.02646 2.8 2.45603 3.09428C1.95426 3.35314 1.54631 3.76619 1.29065 4.27423C1 4.85179 1 5.60786 1 7.12V14.68C1 16.1921 1 16.9482 1.29065 17.5258C1.54631 18.0338 1.95426 18.4469 2.45603 18.7057C3.02646 19 3.77319 19 5.26667 19Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const ExpandIcon = () => (
  <svg viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path d="M17 10.7778V12.7333C17 14.2268 17 14.9735 16.7094 15.544C16.4537 16.0457 16.0457 16.4537 15.544 16.7094C14.9735 17 14.2268 17 12.7333 17H10.7778M7.22222 1H5.26667C3.77319 1 3.02646 1 2.45603 1.29065C1.95426 1.54631 1.54631 1.95426 1.29065 2.45603C1 3.02646 1 3.77319 1 5.26667V7.22222M11.6667 6.33333L17 1M17 1H11.6667M17 1V6.33333M6.33333 11.6667L1 17M1 17H6.33333M1 17L1 11.6667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ViewsIcon = () => (
  <svg viewBox="0 0 22 16" fill="none" aria-hidden="true">
    <path d="M1.26805 8.71318C1.12971 8.49754 1.06054 8.38972 1.02181 8.22342C0.992729 8.0985 0.992729 7.9015 1.02181 7.77658C1.06054 7.61028 1.12971 7.50246 1.26805 7.28682C2.41127 5.50484 5.81418 1 11 1C16.1858 1 19.5887 5.50484 20.7319 7.28682C20.8703 7.50246 20.9395 7.61028 20.9782 7.77658C21.0073 7.9015 21.0073 8.0985 20.9782 8.22342C20.9395 8.38972 20.8703 8.49754 20.7319 8.71318C19.5887 10.4952 16.1858 15 11 15C5.81418 15 2.41127 10.4952 1.26805 8.71318Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M11 11C12.6831 11 14.0475 9.65685 14.0475 8C14.0475 6.34315 12.6831 5 11 5C9.31692 5 7.95251 6.34315 7.95251 8C7.95251 9.65685 9.31692 11 11 11Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const EngagementIcon = () => (
  <svg viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path d="M5.10176 17V8.2M1 9.8V15.4C1 16.2837 1.73457 17 2.6407 17H13.655C14.8697 17 15.9027 16.1357 16.0874 14.9649L16.9709 9.36494C17.2002 7.91112 16.0468 6.6 14.5384 6.6H11.6646C11.2115 6.6 10.8442 6.24183 10.8442 5.8V2.97267C10.8442 1.8832 9.93857 1 8.82137 1C8.5549 1 8.31343 1.15304 8.2052 1.3905L5.31829 7.72491C5.18662 8.01381 4.89283 8.2 4.56864 8.2H2.6407C1.73457 8.2 1 8.91634 1 9.8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const getFill = (sentiment) => sentimentPaletteMap[sentiment] ?? FALLBACK_FILL;
// Stronger tone of the same sentiment; the selected bubble fills with a mid
// tone of it (accent at 35% over the pale fill) and grows slightly.
const getAccent = (sentiment) => dashboardSentimentPaletteMap[sentiment] ?? FALLBACK_STROKE;
const SELECTED_SCALE = 1.1;

/**
 * Seeds bubble positions. The collision layout itself runs live in the
 * component (d3-force ticks -> echarts setOption), so bubbles visibly push
 * each other into place as the old chart did. echarts has no such layout.
 */
const seedNodes = (stories, width, height) => {
  if (stories.length === 0) return [];
  const shareExtent = extent(stories, (s) => s.story_share);
  const radiusScale = scaleLinear().domain(shareExtent).range([MIN_RADIUS, MAX_RADIUS]);
  const dateExtent = extent(stories, (s) => new Date(s.date_created));
  const freshnessScale = scaleTime().domain(dateExtent).range([0.1, 1]);

  return stories.map((story) => {
    const r = shareExtent[0] === shareExtent[1] ? (MIN_RADIUS + MAX_RADIUS) / 2 : radiusScale(story.story_share);
    // Spread narratives left/right, fresher stories nearer the centre (same
    // seeding as the previous d3 chart, so the picture stays familiar).
    const offset = story.narrativeIndex - 0.5;
    const freshness = 1 - freshnessScale(new Date(story.date_created));
    return { id: story.id, r, x: width / 2 + 140 * offset * freshness, y: height / 2 };
  });
};

const clampToBox = (nodes, width, height) => {
  nodes.forEach((n) => {
    n.x = Math.max(n.r, Math.min(width - n.r, n.x));
    n.y = Math.max(n.r, Math.min(height - n.r, n.y));
  });
};

const toGraphData = (stories, nodes, width, selectedId = null) => {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const items = stories
    .map((story) => {
      const pos = byId.get(story.id);
      if (!pos) return null;
      const color = getFill(story.sentiment);
      const accent = getAccent(story.sentiment);
      const isSelected = story.id === selectedId;
      return {
        id: String(story.id),
        name: String(story.id),
        x: pos.x,
        y: pos.y,
        symbolSize: pos.r * 2 * (isSelected ? SELECTED_SCALE : 1),
        // Every property the hover/selected states touch must exist here too:
        // zrender animates state changes per property and throws when the
        // normal value is undefined (seen as "reading 'colorStops'").
        itemStyle: { color, borderColor: color, borderWidth: 0, shadowBlur: 0, shadowColor: 'rgba(0, 0, 0, 0)', opacity: 1 },
        // Repeat the fill so echarts does not lift it to a lighter tint.
        emphasis: { itemStyle: { color, borderColor: color } },
        // Selected: deeper fill of the same sentiment with a thin border in
        // the full accent tone (darker than the fill), no glow.
        select: {
          itemStyle: {
            color: echartsColor.modifyAlpha(accent, 0.35),
            // A step darker than the fill, but still translucent so it stays soft.
            borderColor: echartsColor.modifyAlpha(accent, 0.6),
            borderWidth: 1.5,
            shadowBlur: 0,
            shadowColor: 'rgba(0, 0, 0, 0)',
          },
        },
        story,
      };
    })
    .filter(Boolean);
  // Two invisible corner anchors pin the data bounds to the chart box so the
  // pixel layout is never rescaled by echarts' auto-fit.
  items.push(
    { id: '__nw', x: 0, y: 0, symbolSize: 0, silent: true, itemStyle: { color: FALLBACK_FILL, borderColor: FALLBACK_FILL, borderWidth: 0, opacity: 0 } },
    { id: '__se', x: width, y: CHART_HEIGHT, symbolSize: 0, silent: true, itemStyle: { color: FALLBACK_FILL, borderColor: FALLBACK_FILL, borderWidth: 0, opacity: 0 } },
  );
  return items;
};

export function StoriesBubbleChart({
  data,
  narrative,
  onCircleClick = () => {},
  onTranslationClick = () => {},
  currentUser,
  isShare,
}) {
  const { t } = useTranslation();
  const { editBubbleChartSentiment } = useContext(MessagesContext);

  const wrapperRef = useRef(null);
  const chartRef = useRef(null);
  const closeButtonRef = useRef(null);
  const popupRef = useRef(null);

  const [width, setWidth] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [popupPosition, setPopupPosition] = useState({ left: 0, top: 0 });
  const [infoModal, setInfoModal] = useState({ isActive: false, id: null, title: null });
  const [addToNarrativeModal, setAddToNarrativeModal] = useState({ isActive: false, storyId: null });
  const [addToThreatModal, setAddToThreatModal] = useState({ isActive: false, storyId: null });

  // Flatten narratives -> stories, remembering which narrative each came from.
  const stories = useMemo(() => {
    const list = [];
    (data?.objects ?? []).forEach((entry, narrativeIndex) => {
      (entry.stories ?? []).forEach((story) => {
        list.push({ ...story, narrativeIndex, narrative_id: entry.id });
      });
    });
    return list;
  }, [data?.objects]);

  const currentStory = useMemo(
    () => stories.find((story) => story.id === selectedId) ?? null,
    [stories, selectedId],
  );

  useEffect(() => {
    const element = wrapperRef.current;
    if (!element || typeof window.ResizeObserver === 'undefined') {
      setWidth(element?.clientWidth ?? 900);
      return undefined;
    }
    const observer = new window.ResizeObserver((entries) => {
      const entry = entries.find((e) => e.target === element);
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Layout depends only on ids / sizes / dates, not on translation toggles, so
  // bubbles stay put when a summary is translated.
  const layoutKey = useMemo(
    () => stories.map((s) => `${s.id}:${s.story_share}:${s.date_created}`).join('|'),
    [stories],
  );
  const nodesRef = useRef([]);
  const storiesRef = useRef(stories);
  storiesRef.current = stories;
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;

  // Live collision layout: every d3 tick pushes the current positions into the
  // chart (one setOption per animation frame), so the bubbles settle visibly.
  useEffect(() => {
    if (width <= 0) return undefined;
    const nodes = seedNodes(storiesRef.current, width, CHART_HEIGHT);
    nodesRef.current = nodes;
    if (nodes.length === 0) return undefined;

    let frame = null;
    const push = () => {
      frame = null;
      const chart = chartRef.current?.getEchartsInstance?.();
      chart?.setOption({ series: [{ id: 'bubbles', data: toGraphData(storiesRef.current, nodes, width, selectedIdRef.current) }] });
    };
    const simulation = forceSimulation(nodes)
      .force('center', forceCenter(width / 2, CHART_HEIGHT / 2))
      .force('charge', forceManyBody().strength(3))
      .force('collide', forceCollide().radius((d) => d.r + 2).strength(0.3).iterations(2))
      .on('tick', () => {
        clampToBox(nodes, width, CHART_HEIGHT);
        if (frame === null) frame = window.requestAnimationFrame(push);
      });

    return () => {
      simulation.stop();
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
    // Re-run only when the set of bubbles or the box changes, never on
    // translation / sentiment edits (storiesRef carries those).
  }, [layoutKey, width]);

  const legend = useMemo(
    () => [
      { key: 'POSITIVE', title: t('Positive opinion') },
      { key: 'NEUTRAL', title: t('Neutral opinion') },
      { key: 'NEGATIVE', title: t('Negative opinion') },
    ],
    [t],
  );

  const option = useMemo(() => ({
    aria: { enabled: true },
    // No hover tooltip: the story card on click carries the details.
    tooltip: { show: false },
    series: [
      {
        id: 'bubbles',
        type: 'graph',
        layout: 'none',
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
        roam: false,
        cursor: 'pointer',
        selectedMode: 'single',
        // Positions come from the live simulation (nodesRef); this memo only
        // re-runs on data changes such as translation, using current positions.
        data: toGraphData(stories, nodesRef.current, width, selectedId),
        edges: [],
        // Node names are story ids used for dispatchAction; never draw them.
        label: { show: false },
        emphasis: {
          label: { show: false },
          scale: 1.06,
          itemStyle: { shadowBlur: 10, shadowColor: 'rgba(21, 25, 25, 0.18)' },
        },
        select: { label: { show: false } },
        // Positions are pushed every frame; tweening them would lag behind.
        animationDurationUpdate: 0,
      },
    ],
  }), [stories, width, selectedId]);

  const placePopup = useCallback((x, y, radius) => {
    const wrapper = wrapperRef.current;
    const wrapperWidth = wrapper?.clientWidth ?? width;
    const chartTop = wrapper?.querySelector(`.${styles.chart}`)?.offsetTop ?? 0;
    const fitsRight = x + radius + POPUP_GAP + POPUP_WIDTH <= wrapperWidth;
    const left = fitsRight
      ? x + radius + POPUP_GAP
      : Math.max(POPUP_GAP, x - radius - POPUP_GAP - POPUP_WIDTH);
    // Vertical position is refined once the card is measured (see the layout
    // effect below); start near the bubble.
    const top = chartTop + Math.max(POPUP_GAP, y - 40);
    setPopupPosition({ left, top });
  }, [width]);

  const openStory = useCallback((story, x, y, radius) => {
    if (!story) return;
    placePopup(x, y, radius);
    setSelectedId(story.id);
    onCircleClick(story.id);
    const chart = chartRef.current?.getEchartsInstance?.();
    chart?.dispatchAction({ type: 'select', seriesIndex: 0, name: String(story.id) });
  }, [placePopup, onCircleClick]);

  const closePopup = useCallback(() => {
    if (selectedId === null) return;
    const chart = chartRef.current?.getEchartsInstance?.();
    chart?.dispatchAction({ type: 'unselect', seriesIndex: 0, name: String(selectedId) });
    setSelectedId(null);
    wrapperRef.current?.focus({ preventScroll: true });
  }, [selectedId]);

  const onEvents = useMemo(() => ({
    click: (params) => {
      const story = params?.data?.story;
      if (!story || params.componentType !== 'series') return;
      const node = nodesRef.current.find((n) => n.id === story.id);
      openStory(story, node?.x ?? 0, node?.y ?? 0, node?.r ?? MIN_RADIUS);
    },
  }), [openStory]);

  // Move focus into the card when it opens.
  useEffect(() => {
    if (selectedId !== null) closeButtonRef.current?.focus();
  }, [selectedId]);

  // Keep the whole card inside the chart box: clamp against its real height
  // whenever it changes size ("Show more", translation loaded, etc.).
  const popupText = currentStory?.isTranslation ? currentStory?.translated : currentStory?.summary;
  useLayoutEffect(() => {
    const card = popupRef.current;
    const wrapper = wrapperRef.current;
    if (!card || !wrapper) return undefined;

    const clamp = () => {
      const maxTop = wrapper.clientHeight - card.offsetHeight - POPUP_GAP;
      setPopupPosition((pos) => {
        const top = Math.max(POPUP_GAP, Math.min(pos.top, maxTop));
        return top === pos.top ? pos : { ...pos, top };
      });
    };
    clamp();

    if (typeof window.ResizeObserver === 'undefined') return undefined;
    const observer = new window.ResizeObserver(clamp);
    observer.observe(card);
    return () => observer.disconnect();
  }, [selectedId, popupText]);

  // Keyboard access: bubbles are not focusable in echarts, so the chart box
  // itself takes focus and arrow keys browse stories (highlight + tooltip),
  // Enter/Space opens the card, Escape closes it.
  const handleKeyDown = useCallback((event) => {
    if (stories.length === 0) return;
    const chart = chartRef.current?.getEchartsInstance?.();
    // Only the wrapper itself navigates stories; controls inside the open
    // card (radios, scrollable body, buttons) keep their native key handling.
    const isWrapperTarget = event.target === wrapperRef.current;
    const moveTo = (index) => {
      const n = stories.length;
      const bounded = ((index % n) + n) % n;
      setFocusedIndex(bounded);
      const story = stories[bounded];
      chart?.dispatchAction({ type: 'downplay', seriesIndex: 0 });
      chart?.dispatchAction({ type: 'highlight', seriesIndex: 0, name: String(story.id) });
    };
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        if (!isWrapperTarget) return;
        event.preventDefault();
        moveTo(focusedIndex + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        if (!isWrapperTarget) return;
        event.preventDefault();
        moveTo(focusedIndex - 1);
        break;
      case 'Enter':
      case ' ': {
        if (!isWrapperTarget) return;
        event.preventDefault();
        const story = stories[focusedIndex >= 0 ? focusedIndex : 0];
        const node = nodesRef.current.find((n) => n.id === story?.id);
        if (story) openStory(story, node?.x ?? 0, node?.y ?? 0, node?.r ?? MIN_RADIUS);
        break;
      }
      case 'Escape':
        if (selectedId !== null) {
          event.preventDefault();
          closePopup();
        }
        break;
      default:
        break;
    }
  }, [stories, focusedIndex, openStory, selectedId, closePopup]);

  const saveToThreat = (narrativeId, threatName, threatDescription, caseType, incidentType, topics, geoScope, narratives, campaigns, counterEntities, reviewStatus, declineReason, organizations, isCreatedByUcbiReporter, threatPublicNameEn, threatPublicNameUk, threatPublicDescriptionEn, threatPublicDescriptionUk, threatIsPublic, messages) => {
    const messagesList = Object.keys(messages);
    const payload = {
      name: threatName,
      description: threatDescription,
      is_threat: true,
      is_public: threatIsPublic,
      public_name_en: threatPublicNameEn,
      public_name_uk: threatPublicNameUk,
      public_description_en: threatPublicDescriptionEn,
      public_description_uk: threatPublicDescriptionUk,
      is_created_by_ucbi_reporter: isCreatedByUcbiReporter,
    };
    if (caseType) payload.type = caseType.value;
    if (incidentType) payload.incident_type = incidentType.map((v) => v.value);
    if (topics) payload.topic = topics.map((v) => v.value);
    if (geoScope) payload.geo_scope = geoScope.map((v) => v.value);
    if (narratives) payload.narrative_ids = narratives.map((v) => v.value);
    if (campaigns) payload.campaign_ids = campaigns.map((v) => v.value);
    if (counterEntities) payload.counter_entity_ids = counterEntities.map((v) => v.value);
    if (reviewStatus) payload.review_status = reviewStatus;
    if (declineReason) payload.decline_reason = declineReason;
    if (organizations) payload.workspace_ids = organizations.map((v) => v.value);

    API.fetch('POST', `/API/v1/narratives/${narrativeId}/copy_from_stories`, null, {
      new_narrative: payload,
      existing_narrative_id: null,
      story_ids: messagesList,
    })
      .then((data) => {
        window.open(`/threats/${data.id}`, '_blank');
      })
      .catch((e) => showRequestError(e, t('Stories')));
  };

  const isTranslating = currentStory?.isTranslation === true && !currentStory?.translated;
  const popupDate = currentStory?.first_date
    ? format(parseISO(`${currentStory.first_date}Z`), 'dd LLL yyyy, HH:mm')
    : '';

  return (
    <div
      className={styles.wrapper}
      ref={wrapperRef}
      tabIndex={0}
      role="group"
      aria-label={t('Stories bubble chart')}
      aria-describedby="stories-bubble-chart-hint"
      onKeyDown={handleKeyDown}
    >
      <p id="stories-bubble-chart-hint" className={styles.hint}>
        {t('Use arrow keys to browse stories, Enter to open details, Escape to close them.')}
      </p>

      <ul className={styles.legend} aria-label={t('Sentiment')}>
        {legend.map((item) => (
          <li key={item.key} className={styles.legendItem}>
            <span className={styles.legendSwatch} style={{ backgroundColor: getFill(item.key) }} aria-hidden="true" />
            {item.title}
          </li>
        ))}
      </ul>

      {stories.length === 0 ? (
        <div className={styles.noData}>{t('There is no data to display')}</div>
      ) : (
        <ReactECharts
          ref={chartRef}
          className={styles.chart}
          option={option}
          notMerge={false}
          style={{ width: '100%', height: `${CHART_HEIGHT}px` }}
          opts={{ renderer: 'canvas' }}
          onEvents={onEvents}
        />
      )}

      {currentStory ? (
        <section
          ref={popupRef}
          className={styles.popup}
          style={{ left: popupPosition.left, top: popupPosition.top }}
          role="dialog"
          aria-label={t('Story details')}
        >
          <div className={styles.popupHeader}>
            <div className={styles.popupDate}>
              <CalendarIcon />
              <time dateTime={currentStory.first_date ?? undefined}>{popupDate}</time>
            </div>
            <button
              type="button"
              ref={closeButtonRef}
              className={styles.iconButton}
              aria-label={t('Close')}
              title={t('Close')}
              onClick={closePopup}
            >
              <CloseIcon />
            </button>
          </div>

          <div className={styles.popupBody}>
            {isTranslating ? (
              <div className={styles.loader}><LoaderSmall /></div>
            ) : (
              <ExpandableText
                length={200}
                text={popupText ?? ''}
                textClassName={messageStyles.extendedMessage}
                isLabel={true}
              />
            )}

            <div className={styles.metaRow}>
              <div className={styles.translateToggle} role="group" aria-label={t('Text language')}>
                <button
                  type="button"
                  className={currentStory.isTranslation ? '' : styles.translateActive}
                  aria-pressed={!currentStory.isTranslation}
                  aria-label={t('Original text')}
                  title={t('Original text')}
                  onClick={() => onTranslationClick(currentStory.id, currentStory.summary, false)}
                >
                  <OriginalTextIcon />
                </button>
                <button
                  type="button"
                  className={currentStory.isTranslation ? styles.translateActive : ''}
                  aria-pressed={!!currentStory.isTranslation}
                  aria-label={t('Translation')}
                  title={t('Translation')}
                  onClick={() => onTranslationClick(currentStory.id, currentStory.summary, true)}
                >
                  <TranslationIcon />
                </button>
              </div>
              <button
                type="button"
                className={styles.publicationsButton}
                title={t('Total publications')}
                onClick={() =>
                  setInfoModal({
                    isActive: true,
                    id: currentStory.id,
                    title: String(currentStory.summary ?? '').substring(0, 50),
                  })
                }
              >
                <span className={styles.publicationsLabel}>{t('Total publications')}:</span>
                <span className={styles.publicationsCount}>{formatNumber(currentStory.messages_count)}</span>
                <ExpandIcon />
              </button>
            </div>
          </div>

          <dl className={styles.popupStats}>
            <div className={styles.stat}>
              <dt className={styles.hint}>{t('Sentiment')}</dt>
              <dd className={styles.statValue}>
                {currentStory.sentiment_score !== null && currentStory.sentiment_score !== undefined ? (
                  <Sentiment
                    sentimentScore={currentStory.sentiment_score}
                    messageId={currentStory.id}
                    narrative={narrative}
                    isShare={isShare}
                    isChart={true}
                    isStories={true}
                    onChange={(sentiment, messageId) => editBubbleChartSentiment(sentiment, messageId, narrative)}
                  />
                ) : (
                  <span>—</span>
                )}
              </dd>
            </div>
            <div className={styles.stat}>
              <dt className={styles.hint}>{t('Views')}</dt>
              <dd className={styles.statValue} title={t('Views')}><ViewsIcon />{formatWithCustomGigaBillion(currentStory.impressions_sum)}</dd>
            </div>
            <div className={styles.stat}>
              <dt className={styles.hint}>{t('Engagement')}</dt>
              <dd className={styles.statValue} title={t('Engagement')}><EngagementIcon />{formatWithCustomGigaBillion(currentStory.engagement_sum)}</dd>
            </div>
          </dl>
        </section>
      ) : null}

      {infoModal.isActive ? (
        <NarrativeStoriesModal
          infoModal={infoModal}
          narrative={narrative}
          isShare={isShare}
          onChange={(bool) => setInfoModal({ isActive: bool, id: null, title: null })}
          onAddToCase={(storyId) => {
            setInfoModal({ isActive: false, id: null, title: null });
            setAddToNarrativeModal({ isActive: true, storyId });
          }}
          onAddToThreat={(storyId) => {
            setInfoModal({ isActive: false, id: null, title: null });
            setAddToThreatModal({ isActive: true, storyId });
          }}
        />
      ) : null}

      {addToNarrativeModal.isActive ? (
        <AddToNarrativeModal
          isOpen={addToNarrativeModal}
          messages={addToNarrativeModal.storyId}
          deduplication={true}
          caseId={narrative.id}
          onChange={(bool) => setAddToNarrativeModal(bool)}
        />
      ) : null}

      {addToThreatModal.isActive ? (
        <ThreatEditModal
          isOpen={addToThreatModal}
          title={t('Add to threat')}
          threat={narrative}
          isAdmin={currentUser?.is_super_admin}
          onChange={(bool) => setAddToThreatModal({ isActive: bool, storyId: null })}
          handleThreat={({
            threat, threatName, threatDescription, caseType, incidentType, topics, geoScope,
            narratives, campaigns, counterEntities, reviewStatus, declineReason, organizations,
            isCreatedByUcbiReporter, threatPublicNameEn, threatPublicNameUk,
            threatPublicDescriptionEn, threatPublicDescriptionUk, threatIsPublic,
          }) =>
            saveToThreat(
              threat.id, threatName, threatDescription, caseType, incidentType, topics, geoScope,
              narratives, campaigns, counterEntities, reviewStatus, declineReason, organizations,
              isCreatedByUcbiReporter, threatPublicNameEn, threatPublicNameUk,
              threatPublicDescriptionEn, threatPublicDescriptionUk, threatIsPublic,
              addToThreatModal.storyId,
            )
          }
        />
      ) : null}
    </div>
  );
}
